<template>
  <swiper v-if="mainPageSlides.length" v-bind="swiperOptions" @swiper="onSwiper">
    <swiper-slide v-for="slide in mainPageSlides" :key="slide.id">
      <main-slider-item :slide="slide" :image-list="imageList" :coefficients-data="coefficientsData[slide.eventId]" />
    </swiper-slide>
  </swiper>
  <div v-else class="slider-skeleton"></div>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import getContentList from 'src/apollo/queries/getContentList.gql'
import getImageContentList from 'src/apollo/queries/getImageContentList.gql'
import getSportsbookPromoFeed from 'src/apollo/queries/getSportsbookPromoFeed.gql'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, EffectFade, Pagination } from 'swiper/modules'
import MainSliderItem from 'components/banners/MainSliderItem.vue'
import 'swiper/css/effect-fade'

const props = defineProps({
  showNavigationOnMobile: {
    type: Boolean,
    default: false,
  },
})
const mediaQueries = inject('mediaQueries')
const mainPageSlides = ref([]),
  imageList = ref({}),
  coefficientsData = ref({}),
  swiperInstance = ref(null)

const swiperOptions = computed(() => ({
  class: 'main-slider',
  preloadImages: false,
  loop: true,
  freeMode: false,
  freeModeSticky: false,
  spaceBetween: 8,
  slidesPerView: 1,
  threshold: 10,
  centeredSlides: true,
  effect: 'fade',
  speed: 500,
  pagination: isMobile.value && !props.showNavigationOnMobile ? false : { clickable: true },
  fadeEffect: {
    crossFade: false,
  },
  autoplay: {
    delay: 5000,
    pauseOnMouseEnter: true,
  },
  modules: [Autoplay, EffectFade, Pagination],
}))
const isMobile = computed(() => mediaQueries.mobileSmall)

const markets = computed(() =>
  mainPageSlides.value.map((event) => event.eventId).filter((id) => typeof id === 'string')
)

const { result: banners } = useQuery(getContentList, { type: 'banner' })

function onSwiper(swiper) {
  swiperInstance.value = swiper
}

function getMarket(marketIds) {
  const tournamentIds = marketIds.value.filter((id) => id)
  if (!tournamentIds.length) {
    return
  }
  const { result } = useQuery(getSportsbookPromoFeed, { tournamentIds })
  if (result.value) {
    const sportData = JSON.parse(result.value.sportsbookPromoFeed)
    const filteredSportData = sportData.filter((el) => !!el)
    findMarket(filteredSportData)
  }
}
function findMarket(events) {
  let eventId
  events.forEach((event) => {
    event.forEach((option, index) => {
      if (index === 0) {
        eventId = option[index][0]
        coefficientsData.value[eventId] = []
      } else {
        coefficientsData.value[eventId].push({
          type: option[0][2],
          coefficients: [...Object.values(option[1][''])],
        })
      }
    })
  })
}
function getSlides() {
  const propsContentList = banners.value?.propsContentList ?? []
  const props = propsContentList
    .map((slide) => ({ id: slide.id, ...JSON.parse(slide.props) }))
    .filter((slide) => slide.active)
    .sort((a, b) => a.order - b.order)

  const listId = props.reduce((acc = [], current) => {
    function getId(elem) {
      if (elem?.id) {
        return elem.id
      } else if (typeof elem === 'number') {
        return elem
      } else {
        return null
      }
    }

    const array = [getId(current.image), getId(current.imageBackground), getId(current.imageTop)]
    acc.push(...array)
    return acc
  }, [])

  const uniqueListId = Array.from(new Set(listId)).filter((i) => i !== null)

  const { onResult: images } = useQuery(getImageContentList, {
    id: uniqueListId,
  })
  images(({ data }) => {
    if (data?.imageContentList) {
      const newObject = {}
      data.imageContentList.forEach((i) => {
        newObject[i.id] = i
      })
      imageList.value = newObject
    }
  })

  mainPageSlides.value = props
  getMarket(markets)
}

getSlides()

watch(banners, getSlides)
</script>

<style lang="scss">
.slider-skeleton {
  margin-top: 12px;
  margin-bottom: 40px;
  border-radius: var(--radius-2);
  height: 180px;
  background: var(--cas-main-slider-skeleton);

  @media (min-width: 768px) {
    height: 275px;
  }

  @media (min-width: 1100px) {
    margin-top: 24px;
  }
}

.main-slider {
  width: 100%;
  margin-top: 12px;
  border-radius: var(--radius-2);
  @media (min-width: 1100px) {
    margin-top: 24px;
  }
  .swiper-slide {
    opacity: 0 !important;
    z-index: 1;
  }

  .swiper-slide-active {
    opacity: 1 !important;
    z-index: 2;
  }

  .swiper-pagination {
    margin-top: 16px;
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .swiper-pagination-bullet {
    content: '';
    border-radius: 50%;
    width: 8px;
    height: 8px;
    border: 1px solid var(--cas-light-500);
    display: inline-block;
    margin: 0;
    cursor: pointer;
  }
  .swiper-pagination-bullet-active {
    border-color: var(--cas-secondary);
    background: var(--cas-secondary);
    box-shadow: 0 0 0 2px rgba(#f7b636, 0.35);
  }
}

@media (max-width: 575px) {
  .main-slider {
    margin-bottom: 0;
  }
}
</style>
